import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse } from '../types/api.types';
import {
  AccountType,
  LedgerEntryResponse,
  OwnerType,
  WalletSnapshotResponse,
} from '../types/wallet.types';

/**
 * Wallet self-service de l'owner courant.
 *
 * ⚠️ **Uniquement des endpoints self-scoped** (`/accounts/me`, `/ledger/me`) :
 * l'`ownerRef` est résolu depuis le JWT, donc pas de garde de rôle. Les variantes
 * par acteur (`/accounts/{ownerType}/{ownerRef}`, `/ledger/account/{id}`,
 * `/admin/prestataires-in-debt`) sont gardées `ROLE_ADMIN`/`wallet_read` → un owner
 * prendrait un 403. Ne pas les réintroduire ici (cf. API_REFERENCE §4.2 / §4.9.bis).
 */
@Injectable({ providedIn: 'root' })
export class WalletsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/wallet/api`;

  /** Self-service : wallet du user courant — `GET /accounts/me?ownerType=...`.
   *  Le back résout le `ownerRef` à partir du JWT (claim `nawam_user_id`). */
  getMine(ownerType: OwnerType): Observable<WalletSnapshotResponse> {
    const params = new HttpParams().set('ownerType', ownerType);
    return this.http.get<WalletSnapshotResponse>(
      `${this.base}/accounts/me`,
      { params },
    );
  }

  /**
   * Self-service : grand livre de l'acteur courant — `GET /ledger/me?ownerType=...`.
   * Self-scoped (l'`ownerRef` est résolu depuis le JWT), donc accessible sans
   * rôle admin. `accountType` filtre CREDIT ou BALANCE ; omis → les deux.
   * Enveloppe Nawam (1-indexée).
   */
  ledgerMine(
    ownerType: OwnerType,
    accountType?: AccountType,
    page = 1,
    size = 20,
  ): Observable<ApiPageResponse<LedgerEntryResponse>> {
    let params = new HttpParams()
      .set('ownerType', ownerType)
      .set('page', String(page))
      .set('size', String(size));
    if (accountType) params = params.set('accountType', accountType);
    return this.http.get<ApiPageResponse<LedgerEntryResponse>>(
      `${this.base}/ledger/me`,
      { params },
    );
  }
}
